/**
 * A still image of a Leaflet map -- as JPEG, drawn in the browser.
 *
 * **The server fetches no tiles.** That is the first exception in
 * `CLAUDE.md`, and it stays with the browser: the book page has drawn the
 * map anyway, so it is copied here and sent along as an image. No
 * `html2canvas`, no `foreignObject` (that taints the canvas in Safari and
 * `toDataURL` throws): the tiles are `<img>`, the route an `<svg>`, the
 * pins small SVGs -- each of them can be painted to its place one by one.
 *
 * **The tiles need `crossorigin`** (Leaflet option `crossOrigin`), otherwise
 * the canvas counts as tainted and cannot be read. OpenStreetMap and
 * OpenTopoMap allow it. If it fails anyway, `null` comes back, and the book
 * is built without this map.
 */
export async function mapImage(box: HTMLElement, factor = 2): Promise<string | null> {
  const frame = box.getBoundingClientRect()
  if (frame.width < 10 || frame.height < 10) return null
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(frame.width * factor)
  canvas.height = Math.round(frame.height * factor)
  const ctx = canvas.getContext('2d')
  if (ctx === null) return null
  ctx.scale(factor, factor)
  ctx.fillStyle = '#e8e6e1'
  ctx.fillRect(0, 0, frame.width, frame.height)

  // Whatever sticks out over the edge does not belong in the image.
  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, frame.width, frame.height)
  ctx.clip()

  // 1. The tiles, each at its visible place.
  for (const img of Array.from(box.querySelectorAll<HTMLImageElement>('img.leaflet-tile'))) {
    if (!img.complete || img.naturalWidth === 0) continue
    const r = img.getBoundingClientRect()
    try {
      ctx.drawImage(img, r.left - frame.left, r.top - frame.top, r.width, r.height)
    } catch {
      // A tile without CORS: it is missing from the image, the rest stays.
    }
  }

  // 2. The route: the SVG of the overlay pane, loaded as an image.
  //    Placed by Leaflet's own offsets (`translate3d` of the pane and of the
  //    layer), which read the same in every browser; `paintSvg` strips that
  //    transform from the copy, see there.
  const pane = box.querySelector<HTMLElement>('.leaflet-map-pane')
  const paneShift = pane === null ? { x: 0, y: 0 } : leafletShift(pane)
  for (const svg of Array.from(box.querySelectorAll<SVGSVGElement>('.leaflet-overlay-pane svg'))) {
    const shift = leafletShift(svg)
    const width = Number(svg.getAttribute('width'))
    const height = Number(svg.getAttribute('height'))
    if (width > 0 && height > 0) {
      await paintSvg(ctx, svg, { left: paneShift.x + shift.x, top: paneShift.y + shift.y, width, height })
    } else {
      const r = svg.getBoundingClientRect()
      await paintSvg(ctx, svg, { left: r.left - frame.left, top: r.top - frame.top, width: r.width, height: r.height })
    }
  }

  // 3. The pins: every icon with its computed colours, because a serialised
  //    SVG does not know the page's stylesheet.
  for (const pin of Array.from(box.querySelectorAll<HTMLElement>('.leaflet-marker-icon'))) {
    const svg = pin.querySelector('svg')
    if (svg === null) continue
    const r = svg.getBoundingClientRect()
    await paintSvg(ctx, svg, { left: r.left - frame.left, top: r.top - frame.top, width: r.width, height: r.height })
  }
  ctx.restore()

  // 4. The attribution, bottom right, as on the map.
  const attribution = box.querySelector<HTMLElement>('.leaflet-control-attribution')
  if (attribution !== null) {
    const text = attribution.innerText.replace(/\s+/g, ' ').trim()
    if (text !== '') {
      ctx.font = '9px sans-serif'
      const width = ctx.measureText(text).width + 8
      ctx.fillStyle = 'rgba(255,255,255,0.8)'
      ctx.fillRect(frame.width - width, frame.height - 14, width, 14)
      ctx.fillStyle = '#333'
      ctx.fillText(text, frame.width - width + 4, frame.height - 4)
    }
  }

  try {
    return canvas.toDataURL('image/jpeg', 0.88)
  } catch {
    // The canvas counts as tainted: a tile came without CORS.
    return null
  }
}

/**
 * Where Leaflet put an element: the `translate3d` it wrote into the style,
 * in CSS pixels of the map container. Leaflet positions panes, the drawing
 * layer and the pins this way (`L.DomUtil.setPosition`).
 */
function leafletShift(el: Element): { x: number; y: number } {
  const transform = (el as HTMLElement).style?.transform ?? ''
  const m = /translate3d\((-?[\d.]+)px,\s*(-?[\d.]+)px/.exec(transform) ?? /translate\((-?[\d.]+)px,\s*(-?[\d.]+)px/.exec(transform)
  if (m !== null) return { x: parseFloat(m[1] ?? '0'), y: parseFloat(m[2] ?? '0') }
  const style = (el as HTMLElement).style
  return { x: parseFloat(style?.left ?? '') || 0, y: parseFloat(style?.top ?? '') || 0 }
}

/** An SVG element at the given place on the canvas -- with its colours baked in. */
async function paintSvg(
  ctx: CanvasRenderingContext2D,
  svg: SVGSVGElement,
  at: { left: number; top: number; width: number; height: number },
): Promise<void> {
  if (at.width === 0 || at.height === 0) return
  const copy = svg.cloneNode(true) as SVGSVGElement
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  /*
   * **The inline style goes -- it is the reason the route sat shifted.**
   * From use, with a screenshot from the iPad: "die Route ist verschoben,
   * die Markierungen sind noch korrekt." Leaflet moves its drawing layer
   * with `style="transform: translate3d(x, y, 0)"`, and a serialised SVG
   * keeps that style on its root: loaded as an image, the browser applies
   * it a second time, by exactly the layer's offset. The pins have no such
   * style, which is why they were right. Measured pixel by pixel in
   * `tools/pruefstand/routenbild.mjs`, in Chromium -- it was never a Safari
   * quirk.
   */
  copy.removeAttribute('style')
  copy.setAttribute('width', String(at.width))
  copy.setAttribute('height', String(at.height))
  // The computed colours onto every child -- classes from the stylesheet do
  // not arrive in the image.
  const source = Array.from(svg.querySelectorAll<SVGElement>('path, circle, text, polyline, polygon'))
  const target = Array.from(copy.querySelectorAll<SVGElement>('path, circle, text, polyline, polygon'))
  source.forEach((el, i) => {
    const style = getComputedStyle(el)
    const t = target[i]
    if (t === undefined) return
    for (const name of ['fill', 'stroke', 'stroke-width', 'stroke-opacity', 'fill-opacity', 'stroke-linecap', 'stroke-linejoin', 'font-size', 'font-family', 'font-weight'] as const) {
      const value = style.getPropertyValue(name)
      if (value !== '') t.setAttribute(name, value)
    }
  })
  const text = new XMLSerializer().serializeToString(copy)
  const img = new Image()
  const loaded = new Promise<void>((resolve) => {
    img.onload = () => resolve()
    img.onerror = () => resolve()
  })
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(text)
  await loaded
  if (img.naturalWidth === 0 && img.width === 0) return
  try {
    ctx.drawImage(img, at.left, at.top, at.width, at.height)
  } catch {
    // An SVG the browser refused to paint: then the route is missing.
  }
}
